/**
 * Phase 10 — invoice + margin helpers.
 *
 * Pure functions, dependency-free. Drive the Finance tab's invoice
 * sub-section + the per-project Margins panel.
 *
 * Margin model (see KB `architecture/margin-computation.md`):
 *   revenue(designation) = Σ invoice.amount (EUR)
 *   cost(designation)    = hoursSpent × resolvedRate
 *   margin(designation)  = revenue − cost
 */

import type {
  Designation,
  HourlyRate,
  Invoice,
  ProgramType,
} from '@altyor-kronos/shared-types';
import { resolveHourlyRate } from './rateResolver';

// ─── Rounding ─────────────────────────────────────────────────────

const round2 = (n: number) => Math.round(n * 100) / 100;

// ─── sumInvoicesInEUR ─────────────────────────────────────────────

/**
 * Sum all invoice amounts (all invoices are EUR).
 */
export function sumInvoicesInEUR(invoices: readonly Invoice[]): number {
  let total = 0;
  for (const inv of invoices) {
    total += inv.invoiceAmount;
  }
  return round2(total);
}

// ─── remainingToInvoice / invoicedPercent ─────────────────────────

const round1 = (n: number) => Math.round(n * 10) / 10;

/**
 * "RI" — remaining amount to invoice for a designation.
 *   max(orderedAmount − alreadyInvoicedEUR, 0), rounded to 2 decimals.
 * Non-finite inputs are treated as 0 so the UI never renders NaN.
 */
export function remainingToInvoice(
  orderedAmount: number,
  alreadyInvoicedEUR: number,
): number {
  const ordered = Number.isFinite(orderedAmount) ? Math.max(orderedAmount, 0) : 0;
  const invoiced = Number.isFinite(alreadyInvoicedEUR) ? Math.max(alreadyInvoicedEUR, 0) : 0;
  return round2(Math.max(ordered - invoiced, 0));
}

/**
 * "Already invoiced %" — invoiced / ordered × 100, rounded to 1
 * decimal. Returns 0 when orderedAmount ≤ 0 (never NaN/Infinity).
 * Deliberately NOT capped at 100: over-invoicing is a real signal.
 */
export function invoicedPercent(
  alreadyInvoicedEUR: number,
  orderedAmount: number,
): number {
  if (!Number.isFinite(orderedAmount) || orderedAmount <= 0) return 0;
  const invoiced = Number.isFinite(alreadyInvoicedEUR) ? Math.max(alreadyInvoicedEUR, 0) : 0;
  return round1((invoiced / orderedAmount) * 100);
}

// ─── designationMargin ────────────────────────────────────────────

export interface MarginResult {
  /** Revenue in EUR — sum of matching invoices converted. */
  revenueEUR: number;
  /** Cost in EUR — hoursSpent × resolvedRate. */
  costEUR: number;
  /** Margin = revenue − cost, in EUR. */
  marginEUR: number;
}

// ─── designationCost ──────────────────────────────────────────────

/**
 * Cost for a single designation in EUR.
 *   - Fixed designations have zero cost (billed as a lump sum).
 *   - Hourly designations: hoursSpent × resolved rate.
 */
export function designationCost(
  d: Designation,
  rules: readonly HourlyRate[],
  program: ProgramType,
  clientIsInternal: boolean,
): number {
  if (d.billingMode === 'fixed') return 0;
  const hours = d.hoursSpent ?? 0;
  const { amount } = resolveHourlyRate(
    { section: d.section, program, clientIsInternal },
    rules,
    d.hourlyRateOverride,
  );
  return hours * amount;
}

export function designationMargin(
  designation: Designation,
  invoices: readonly Invoice[],
  rules: readonly HourlyRate[],
  program: ProgramType,
  clientIsInternal: boolean,
): MarginResult {
  const matching = invoices.filter((i) => i.designationId === designation.id);
  const revenueEUR = sumInvoicesInEUR(matching);
  const costEUR = round2(designationCost(designation, rules, program, clientIsInternal));
  const marginEUR = round2(revenueEUR - costEUR);
  return { revenueEUR, costEUR, marginEUR };
}

// ─── projectMargin ────────────────────────────────────────────────

export function projectMargin(
  designations: readonly Designation[],
  invoices: readonly Invoice[],
  rules: readonly HourlyRate[],
  program: ProgramType,
  clientIsInternal: boolean,
): MarginResult {
  let revenueEUR = 0;
  let costEUR = 0;
  for (const d of designations) {
    const m = designationMargin(d, invoices, rules, program, clientIsInternal);
    revenueEUR += m.revenueEUR;
    costEUR += m.costEUR;
  }
  return {
    revenueEUR: round2(revenueEUR),
    costEUR: round2(costEUR),
    marginEUR: round2(revenueEUR - costEUR),
  };
}

// ─── Sub-contracting totals ───────────────────────────────────────

export interface SubContractingTotals {
  /** Number of designations flagged `subContracting=true`. */
  count: number;
  /** Sum of their orderedAmount. Assumed EUR already (the field is
   *  currency-less in the shape). */
  totalOrderedEUR: number;
}

export function subContractingTotals(
  designations: readonly Designation[],
): SubContractingTotals {
  let count = 0;
  let total = 0;
  for (const d of designations) {
    if (!d.subContracting) continue;
    count += 1;
    total += d.orderedAmount ?? 0;
  }
  return { count, totalOrderedEUR: round2(total) };
}
